import { useCallback, useRef } from 'react';
import {
  PageHeader as LibPageHeader, SvgIcon,
  useModalToggle,
  useVisibilityRefresh,
  WalletButton
} from '@make-software/tg-mini-app-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import {
  formatHash,
  useMe,
  useSeasonInfo,
  useWalletConnection,
  WalletModal
} from '@/shared';
import ChevronLeft from '@/shared/assets/icons/chevron-left.svg';
import EarnIcon from '@/shared/assets/icons/ic-earn.svg';
import UserIcon from '@/shared/assets/icons/ic-user.svg';
import { ACTIVITY_PATH } from '@/app/paths';
import { formatFansBadge } from '@/utils';

import { AccountContainer, ActivityText } from './page-header.styled';
import { createHiddenSessionCloseTracker } from './page-header.utils';

interface HeaderProps {
  hasBackButtonTo?: number | string;
}

export const PageHeader = ({ hasBackButtonTo }: HeaderProps) => {
  const { data: airdropInfo } = useSeasonInfo();
  const meService = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const hiddenSessionCloseTracker = useRef(createHiddenSessionCloseTracker());

  const isActivityPath = location.pathname === ACTIVITY_PATH;
  const {
    open: openModal,
    openModal: handleOpenModal,
    closeModal: handleCloseModal,
    setOpen
  } = useModalToggle('wallet-modal');

  const userName = meService.data?.user.username;
  const userPhoto = meService.data?.user.photo_url;
  const theme = useTheme();

  const { isWalletConnected, isCsprclickConnected, connectedAccount, connectWallet, downloadWallet } =
    useWalletConnection();

  useVisibilityRefresh(meService.refresh, !connectedAccount && openModal);

  const handleRedirection = useCallback(
    (path: number | string) => {
      if (hiddenSessionCloseTracker.current.recordTrigger(Date.now())) {
        localStorage.removeItem('session');
        alert('sesson closed. refresh to authenticate again')
      }

      navigate(path as any);
    },
    [navigate]
  );

  const handleConnectWallet = useCallback(async () => {
    setOpen(false);
    await connectWallet();
  }, [connectWallet, setOpen]);

  const handleDownloadWallet = useCallback(async () => {
    setOpen(false);
    downloadWallet();
  }, [connectWallet, setOpen]);


  const roundColor = airdropInfo?.airdrop?.round === 1 ? '#FB6F6A' : '#A0B3FF';
  const roundBalance =
    airdropInfo?.airdrop?.round === 1
      ? meService?.userRound1PointBalance
      : meService?.userRound2PointBalance;

  const walletBtnColor = isWalletConnected ? theme.colors.contentGreen : '#A9AAAD';

  return (
    <>
      <LibPageHeader
        username={userName}
        photoUrl={userPhoto}
        balances={[
          { value: meService?.userPointBalance ?? 0, iconSrc: EarnIcon, iconColor: '#F1BF0B' },
          { value: roundBalance ?? 0, iconSrc: EarnIcon, iconColor: roundColor }
        ]}
        hasBackButtonTo={hasBackButtonTo}
        onBackClick={handleRedirection}
        onUserClick={() => handleRedirection(ACTIVITY_PATH)}
        userNameFont={{ fontSize: '12px', fontFamily: 'Inter', fontWeight: 600 }}
        userBalanceFont={{
          fontSize: '12px',
          fontFamily: 'Inter',
          color: '#84868c',
          fontWeight: 500
        }}
        walletSlot={
          <WalletButton
            color={walletBtnColor}
            borderColor={walletBtnColor}
            borderRadius={'20px'}
            status={isWalletConnected ? 'connected' : isCsprclickConnected ? 'partial' : 'disconnected'}
            onClick={handleOpenModal}>
            {connectedAccount
              ? meService?.data?.wallet?.cspr_name
                ? formatFansBadge(meService?.data?.wallet?.cspr_name)
                : formatHash(connectedAccount, 5)
              : 'Connect Wallet'}
          </WalletButton>
        }
        extraSlot={
          !isActivityPath && (
            <AccountContainer
              onClick={() => handleRedirection(ACTIVITY_PATH)}
              gap={10}
              align="center">
              <SvgIcon src={UserIcon} />
              <ActivityText size="md" weight={3}>
                Activity
              </ActivityText>
            </AccountContainer>
          )
        }
        background="#1C1D24"
        backAccentColor="#FB6F6A"
        chevronIconSrc={ChevronLeft}
      />
      <WalletModal
        handleCloseModal={handleCloseModal}
        openModal={openModal}
        telegramName={userName || ''}
        connectedWallet={connectedAccount}
        onConnectWallet={handleConnectWallet}
        onDisconnectWallet={handleConnectWallet}
        onDownloadWallet={handleDownloadWallet}
        unknownWallet={!connectedAccount}
      />
    </>
  );
};
